import React from 'react';
import {
  CSV_LIMIT_EXCEEDED,
  ISSUES_FETCH_REPORT_API,
  ISSUES_FETCH_REPORT_API_FULFILLED,
  ISSUES_FETCH_REPORT_API_REJECTED,
  ISSUES_FETCH_REPORT_START,
  showNotificationAction,
  trackEvent
} from '../actions';
import {toIsoDateWithTime} from '../../utils/date-helpers';
import i18n from '../../utils/i18n';
import {getCurrentViewType, getFetchParams, getProjectName} from '../selectors';
import {getPromiseForAsyncNotification, trimFileName} from '../../utils/notificationHelpers';
import {NotificationLevels, NotificationTypes} from '@adsk/bim360-matrix-react-notification-manager';
import Text from '@adsk/bim360-matrix-react-text';
import {exportList} from '../../utils/analytics-helpers';
import {reportFormats} from '../../constants/analytics';
import {downloadFile} from '../../utils/downloadFile';
import TryAgainErrorNotification from '../../components/common/TryAgainErrorNotification';
import {ISSUE_ATTRIBUTE_NAMES} from '../../constants/issue-attribute-names';
import {CSV_ISSUE_LIMIT} from '../../constants/csv-report';

export default jsonApi => store => next => action => {
  const state = store.getState();

  switch (action.type) {
    case ISSUES_FETCH_REPORT_START: {
      const today = toIsoDateWithTime(Date.now());
      const textIssueType = i18n.t(['issue.label_capitalized', 'issueType.issues.label_capitalized']);
      const query = getFetchParams(state);
      const queryParams = {
        ...query.queryParams,
        ...query.hiddenQueryParams,
        csvVersion: 1,
        sort: `-${ISSUE_ATTRIBUTE_NAMES.ID}`,
        format: 'csv'
      };
      const csvQuery = {
        queryParams
      };

      if (query.targetUrn) {
        csvQuery.targetUrn = query.targetUrn;
        csvQuery.documentVersion = query.documentVersion;
      }

      const csvExportAsyncNotificationPromise = getPromiseForAsyncNotification();
      const fileName = `${getProjectName(state)} - ${textIssueType} - ${today}.csv`;

      store.dispatch(
        showNotificationAction({
          type: NotificationTypes.TOAST,
          level: NotificationLevels.SUCCESS,
          message: (
            <Text hasHTML>
              {i18n.t('notifications.preparingToDownload', {
                fileName: trimFileName(fileName, 35)
              })}
            </Text>
          ),
          autoDismiss: 0.5,
          asyncOperation: csvExportAsyncNotificationPromise.promise,
          closeButtonTooltipText: i18n.t('notifications.downloadWillContinue')
        })
      );

      store.dispatch({
        type: ISSUES_FETCH_REPORT_API,
        meta: {
          fileName,
          csvExportAsyncNotificationPromise
        },
        payload: jsonApi.getCsvQualityIssues(csvQuery) // TODO: right now this is only supported in v1 - address this
      });

      const viewType = getCurrentViewType(state);
      store.dispatch(trackEvent(exportList(reportFormats.CSV, viewType)));
      break;
    }
    case ISSUES_FETCH_REPORT_API_FULFILLED: {
      action.meta.csvExportAsyncNotificationPromise.resolve();
      downloadFile(action.payload[0].payload, action.meta.fileName, 'text/csv;encoding:utf-8');
      break;
    }
    case ISSUES_FETCH_REPORT_API_REJECTED: {
      const {csvExportAsyncNotificationPromise, fileName} = action.meta;

      csvExportAsyncNotificationPromise.reject();
      store.dispatch(
        showNotificationAction({
          type: NotificationTypes.SNACKBAR,
          level: NotificationLevels.ERROR,
          message: (
            <TryAgainErrorNotification
              message={i18n.t('notifications.failedToDownload', {
                fileName: trimFileName(fileName, 35)
              })}
              tryAgainAction={() =>
                store.dispatch({
                  type: ISSUES_FETCH_REPORT_START
                })}
            />
          )
        })
      );
      break;
    }
    case CSV_LIMIT_EXCEEDED: {
      store.dispatch(
        showNotificationAction({
          type: NotificationTypes.SNACKBAR,
          level: NotificationLevels.ERROR,
          message: (
            <Text>
              {i18n.t('notifications.csvIssueLimitError', {
                csvIssueLimit: CSV_ISSUE_LIMIT
              })}
            </Text>
          )
        })
      );
      break;
    }
    default:
      break;
  }

  return next(action);
};
